import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';

import { useDataLoader } from '../../providers/DataLoaderProvider';
import { useFormState } from '../../providers/FormStateProvider';

import { BulkApplyFilters, ConfigurationsTable, SlicesToApplyTable } from './components';
import { useBulkApplyConfigs, useBulkApplySave, useSelectionState } from './hooks';
import { buildSelectedSliceInfo, filterConfigs, getCopyableSlices, getMatchingSlices } from './utils';

/**
 * Dialog for bulk applying slice changes to other configurations.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the dialog is open
 * @param {() => void} props.onClose - Callback when dialog is closed
 * @returns {JSX.Element}
 */
function BulkApplyDialog({ isOpen, onClose }) {
    const { packGroupDate } = useDataLoader();
    const { formData } = useFormState();

    // Filter state
    const [filterWeekDate, setFilterWeekDate] = useState(null);
    const [filterPackGroup, setFilterPackGroup] = useState('all');
    const [filterSegment, setFilterSegment] = useState('all');
    const [weekPopoverOpen, setWeekPopoverOpen] = useState(false);
    const [includeProducts, setIncludeProducts] = useState(false);

    // Config fetching
    const {
        allConfigs,
        copyableSliceIds,
        loading,
        originalSliceNames,
        packGroups,
        reset: resetConfigs,
    } = useBulkApplyConfigs(isOpen, filterWeekDate, formData?.id);

    // Selection state
    const sliceSelection = useSelectionState();
    const configSelection = useSelectionState();

    // Save state
    const { allComplete, executeBulkApply, hasErrors, resetStatuses, saveStatuses, saving } = useBulkApplySave();

    // Initialize filter week to current pack group date when dialog opens
    useEffect(() => {
        if (isOpen && packGroupDate && !filterWeekDate) {
            setFilterWeekDate(packGroupDate);
        }
    }, [isOpen, packGroupDate, filterWeekDate]);

    // Initialize slice selection when copyable slice IDs are loaded
    useEffect(() => {
        if (copyableSliceIds.size > 0) {
            sliceSelection.selectItems(copyableSliceIds);
        }
    }, [copyableSliceIds]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!isOpen) {
            setFilterWeekDate(null);
            setFilterPackGroup('all');
            setFilterSegment('all');
            setIncludeProducts(false);
            resetConfigs();
            sliceSelection.clear();
            configSelection.clear();
            resetStatuses();
        }
    }, [isOpen]);

    // Filter configs based on selected filters
    const configs = useMemo(
        () => filterConfigs(allConfigs, filterPackGroup, filterSegment),
        [allConfigs, filterPackGroup, filterSegment],
    );

    // Build list of copyable slices from current config
    const copyableSlices = useMemo(
        () => getCopyableSlices(formData?.slices, originalSliceNames),
        [formData?.slices, originalSliceNames],
    );

    // Build map of selected slices info for matching
    const selectedSliceInfo = useMemo(
        () => buildSelectedSliceInfo(sliceSelection.selected, originalSliceNames, formData?.slices),
        [sliceSelection.selected, originalSliceNames, formData?.slices],
    );

    // Get matching slices for a config
    const getConfigMatchingSlices = (config) => getMatchingSlices(config, selectedSliceInfo);

    // Configs that have matching slices
    const configsWithMatches = useMemo(
        () => configs.filter((config) => getConfigMatchingSlices(config).length > 0),
        [configs, selectedSliceInfo],
    );

    // Track previous configsWithMatches IDs to detect new/removed configs
    const prevConfigIdsRef = useRef(new Set());

    // Auto-select NEW configs, deselect REMOVED configs, preserve user's manual selections
    useEffect(() => {
        const currentIds = new Set(configsWithMatches.map((c) => c.id));
        const prevIds = prevConfigIdsRef.current;

        configSelection.setSelected((prev) => {
            const next = new Set(prev);

            // Remove configs that are no longer in the list
            prev.forEach((id) => {
                if (!currentIds.has(id)) {
                    next.delete(id);
                }
            });

            // Auto-select configs that are NEW (just appeared)
            currentIds.forEach((id) => {
                if (!prevIds.has(id)) {
                    next.add(id);
                }
            });

            return next;
        });

        prevConfigIdsRef.current = currentIds;
    }, [configsWithMatches]);

    // Handle week change
    const handleWeekChange = (date) => {
        setFilterWeekDate(date);
        // Reset pack group filter when week changes since pack groups may differ
        setFilterPackGroup('all');
    };

    // Handle apply changes
    const handleApplyChanges = () => {
        executeBulkApply({
            currentConfig: formData,
            targetConfigs: configs,
            selectedConfigIds: configSelection.selected,
            selectedSliceIds: sliceSelection.selected,
            originalSliceNames,
            includeProducts,
        });
    };

    // Slice selection handlers
    const handleToggleSlice = (sliceId) => !saving && sliceSelection.toggle(sliceId);
    const handleToggleAllSlices = () => sliceSelection.toggleAll(copyableSlices.map((s) => s.id));

    // Config selection handlers
    const handleToggleConfig = (configId) => !saving && configSelection.toggle(configId);
    const handleToggleAllConfigs = () => configSelection.toggleAll(configsWithMatches.map((c) => c.id));

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !saving && !open && onClose()}>
            <DialogContent className="sm:max-w-5xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Apply Changes to Other Configurations</DialogTitle>
                    <DialogDescription>
                        Changes will be applied to slices with matching names in the selected configurations.
                    </DialogDescription>
                </DialogHeader>

                <BulkApplyFilters
                    filterWeekDate={filterWeekDate}
                    onWeekChange={handleWeekChange}
                    weekPopoverOpen={weekPopoverOpen}
                    setWeekPopoverOpen={setWeekPopoverOpen}
                    filterPackGroup={filterPackGroup}
                    onPackGroupChange={setFilterPackGroup}
                    packGroups={packGroups}
                    filterSegment={filterSegment}
                    onSegmentChange={setFilterSegment}
                    includeProducts={includeProducts}
                    onIncludeProductsChange={setIncludeProducts}
                    disabled={saving || loading}
                />

                {loading && (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="size-6 animate-spin text-muted-foreground" />
                        <span className="ml-2 text-muted-foreground">Loading configurations...</span>
                    </div>
                )}

                {!loading && (
                    <div className="flex-1 min-h-0 flex gap-4 overflow-hidden">
                        <SlicesToApplyTable
                            copyableSlices={copyableSlices}
                            selectedSlices={sliceSelection.selected}
                            onToggleSlice={handleToggleSlice}
                            onToggleAll={handleToggleAllSlices}
                            originalSliceNames={originalSliceNames}
                            allSlices={formData?.slices}
                            disabled={saving}
                        />

                        <ConfigurationsTable
                            currentConfig={formData}
                            configsWithMatches={configsWithMatches}
                            selectedConfigs={configSelection.selected}
                            onToggleConfig={handleToggleConfig}
                            onToggleAll={handleToggleAllConfigs}
                            saveStatuses={saveStatuses}
                            getMatchingSlices={getConfigMatchingSlices}
                            disabled={saving}
                        />
                    </div>
                )}

                <DialogFooter>
                    {allComplete ? (
                        <Button onClick={onClose}>{hasErrors ? 'Close' : 'Done'}</Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={onClose} disabled={saving}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleApplyChanges}
                                disabled={loading || saving || configSelection.size === 0 || sliceSelection.size === 0}
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="size-4 animate-spin" />
                                        Applying...
                                    </>
                                ) : (
                                    `Apply to ${configSelection.size + 1} configuration${configSelection.size !== 0 ? 's' : ''}`
                                )}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default BulkApplyDialog;
